/* FileMultiEditViewRenderer.js */

"use strict";
const _ = require("lodash");
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const Grid = require("@gluenetworks/steve_ui_widgets").GridGl.Grid;
const FormsComponent = require("@gluenetworks/forms").ui.multiEditComponent;

class FileMultiEditViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("file-multiedit-view-gl");
        this._rootElem.component = this._model;

        this._detailSection = document.createElement("div");
        this._detailSection.classList.add("form-section");

        this._selectedFilesSection = document.createElement("div");
        this._selectedFilesSection.classList.add("selected-files-view-gl", "hidden");

        this._logger = this._model.logger;

        this._formsComponent = new FormsComponent();
        this._viewButtonContainer = document.createElement("view-button-container");
        this._viewAllButton = new ButtonControl({
            label: "View All",
            darkTheme: false,
            disabled: true
        });
        this._viewButtonContainer.appendChild(this._viewAllButton.renderer.rootElement);
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Save",
            name: "SaveFilesDetails",
            handler: this._onFilesDetailsSave.bind(this)
        });
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Cancel",
            name: "CancelFilesDetails",
            handler: this._onFileDetailsCancel.bind(this)
        });

        let columnConfig = {
            name: {
                label: "Selected Files",
                width: 1
            }
        };

        this._selectListGrid = new Grid({
            columns: columnConfig,
            showHeader: true,
            columnOrder: ["name"]
        });
        this._gridRows = {};

        this._renderingForm = false;
        this._toggleActions();
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on("EditorModeChanged", (this._onEditorModeChanged = this._onEditorModeChanged.bind(this)));
        this._model.on("Dirty", (this._onDirty = this._onDirty.bind(this)));
        this._viewAllButton.on("UserClick", (this._onViewButtonClick = this._onViewButtonClick.bind(this)));
        this._formsComponent.on(
            "PendingPatchesChanged",
            (this._onFormsPendingPatchesChanged = _.debounce(this._onFormsPendingPatchesChanged.bind(this), 500, false))
        );
    }

    _unregisterListeners() {
        this._model.removeListener("EditorModeChanged", this._onEditorModeChanged);
        this._model.removeListener("Dirty", this._onDirty);
        this._viewAllButton.removeListener("UserClick", this._onViewButtonClick);
        this._formsComponent.removeListener("PendingPatchesChanged", this._onFormsPendingPatchesChanged);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        this._onFormsPendingPatchesChanged.flush();
        let promArr = [this._formsComponent.shutdown()];

        return Promise.all(promArr).then(
            () => {
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._viewAllButton = undefined;
                this._viewButtonContainer.remove();
                this._viewButtonContainer = undefined;
                this._detailSection.remove();
                this._detailSection = undefined;
                this._selectedFilesSection.remove();
                this._selectedFilesSection = undefined;
                this._selectListGrid.shutdown().then(() => {
                    this._selectListGrid = undefined;
                });
            },
            (error) => {
                this._logger.warn({ error }, "FileDetailsViewRenderer.shutdown() error.");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return Promise.resolve()
            .then(() => this._showLoader())
            .then(() => this._model.whenModelIsReady())
            .then(() => {
                this._viewAllButton.setDisabled(_.isEmpty(this._model.osImages));
                this._renderFilesDetails();
                this._renderGridRows();
                if (!this._rootElem.hasChildNodes()) {
                    this._detailSection.appendChild(this._formsComponent.renderer.rootElement);
                    this._detailSection.appendChild(this._viewButtonContainer);
                    this._rootElem.appendChild(this._detailSection);
                    this._rootElem.appendChild(this._selectedFilesSection);
                }
            })
            .then(() => this._hideModal())
            .catch((err) => this._showError(err))
            .then(() => this._toggleActions());
    }

    _onEditorModeChanged() {
        return Promise.resolve().then(() => {
            if (this._model.editorMode === "viewall") {
                this._renderGridRows();
                this._detailSection.classList.add("hidden");
                this._selectedFilesSection.classList.remove("hidden");
                this._renderBackButton();
            } else {
                this._detailSection.classList.remove("hidden");
                this._selectedFilesSection.classList.add("hidden");
            }
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE ACTION METHODS            */
    /*******************************************/

    _showViewActions() {
        this._model.gameEditor.showViewAction("SaveFilesDetails");
        this._model.gameEditor.showViewAction("CancelFilesDetails");
    }

    _hideViewActions() {
        this._model.gameEditor.hideViewAction("SaveFilesDetails");
        this._model.gameEditor.hideViewAction("CancelFilesDetails");
    }

    _disableViewActions() {
        this._model.gameEditor.disableViewAction("SaveFilesDetails");
        this._model.gameEditor.disableViewAction("CancelFilesDetails");
    }

    _enableViewActions() {
        this._model.gameEditor.enableViewAction("SaveFilesDetails");
        this._model.gameEditor.enableViewAction("CancelFilesDetails");
    }

    _toggleActions() {
        if (this._model.isDirty()) {
            this._showViewActions();
        } else {
            this._hideViewActions();
        }
    }
    /*******************************************/
    /* END PRIVATE ACTION METHODS              */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _renderFilesDetails() {
        this._model.setTabDirty(false);

        return Promise.resolve()
            .then(
                () => {
                    let jtype = this._getJtype();
                    let pristineValues = this._model.osImagesPristineProperties;
                    this._formsComponent.setTypeInformation(jtype, true);
                    this._formsComponent.setPristineValue(pristineValues, true);

                    this._renderingForm = true;
                    return this._formsComponent.renderer.render().then(() => {
                        this._renderingForm = false;
                    });
                },
                (err) => {
                    this._logger.warn({ error: err }, "FilesDetailsViewRenderer._renderFilesDetails() error.");
                }
            )
            .then(() => this._enableViewActions());
    }
    _renderGridRows() {
        this._gridRows = {};
        let data = this._model.osImages;
        data.forEach((obj) => {
            this._gridRows[obj.id] = {
                name: obj.name,
                description: obj.description
            };
        });
        this._selectListGrid.setRows(this._gridRows);
        this._selectedFilesSection.appendChild(this._selectListGrid.renderer.rootElement);
    }
    _showLoader(options) {
        return this._model._modalUserFeedback.displayLoading(options);
    }

    _showError(error) {
        return this._model._modalUserFeedback.displayError(error);
    }

    _hideModal() {
        return this._model._modalUserFeedback.hideDisplayed();
    }

    _renderBackButton() {
        const model = this._model;
        const bcs = [
            {
                content: backButton,
                left: {
                    arrow: {
                        direction: "left"
                    }
                }
            }
        ];
        this._model.gameEditor.setBreadCrumbs(bcs);

        return Promise.resolve();

        function backButton() {
            const el = document.createElement("span");
            el.innerHTML = "Multi-Edit";
            el.addEventListener("click", () => {
                // clear breadcrumbs link
                model.gameEditor.setBreadCrumbs([]);
                // change tab back to general
                model.setEditorMode("general");
            });
            // prettier-ignore
            this.renderer = { //jshint ignore:line
                rootElement: el
            };
        }
    }
    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/
    _onViewButtonClick() {
        this._model.setEditorMode("viewall");
    }

    _onFormsPendingPatchesChanged() {
        if (!this._renderingForm) {
            const isDirty = !_.isEmpty(this._getFormValue());
            this._toggleActions();
            this._model.setTabDirty(isDirty);
        }
    }

    _onDirty() {
        this._toggleActions();
    }

    _onFilesDetailsSave() {
        this._disableViewActions();
        return Promise.resolve()
            .then(() => this._showLoader())
            .then(() => this._model.saveOsImagesData(this._getFormValue()))

            .catch((error) => {
                this._logger.warn({ error });
                return this._showError(error);
            })
            .catch((err = {}) => (err.userRejected ? Promise.resolve() : Promise.reject(err)))

            .then(() => this._renderFilesDetails())
            .then(() => this._hideModal())
            .then(() => _.isEmpty(this._model.osImages) && this._model.closeTab())

            .catch((error) => this._logger.warn({ error }, "FileDetailsViewRender._onFileDetailsSave() error."));
    }

    _onFileDetailsCancel() {
        this._model.gameEditor.runQuickConfirmationActionWorkflow(
            {
                message: "Are you sure you want to cancel? All changes will be lost.",
                title: "Cancel Confirmation Check",
                confirmDetails: {
                    text: "Yes",
                    type: "text"
                }
            },
            this._onFileDetailsCancelAction.bind(this)
        );
    }

    _onFileDetailsCancelAction() {
        return this._renderFilesDetails();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/
    _getJtype() {
        const jtype = this._model.getOsImageJType();
        jtype[`name@Display.readOnly`] = true;

        return jtype;
    }

    _getFormValue() {
        return this._formsComponent.getFormValue();
    }
    /*******************************************/
    /* END PRIVATE API METHODS                 */
    /* START PUBLIC API METHODS                */
    /*******************************************/
    getFormData() {
        return this._getFormValue();
    }
    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = FileMultiEditViewRenderer;
